import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PawLinkLogo } from "@/components/PawLinkLogo";
import { ArrowRight, EnvelopeSimple, Lock, User } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import Nav from "@/components/Nav";
import GoogleButton from "@/components/GoogleButton";

export default function Register() {
    const { register } = useAuth();
    const { t, lang } = useI18n();
    const nav = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        const res = await register(email, password, name, lang);
        setLoading(false);
        if (!res.ok) return setError(res.error);
        nav("/home");
    };

    return (
        <div className="min-h-screen bg-paw-bone dark:bg-paw-night">
            <Nav />

            <div aria-hidden className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-24 -start-24 w-[420px] h-[420px] rounded-full bg-paw-terra/12 dark:bg-paw-terra/[0.06] blur-[120px]" />
                <div className="absolute bottom-0 -end-32 w-[520px] h-[520px] rounded-full bg-paw-terra/8 dark:bg-paw-terra/[0.04] blur-[140px]" />
            </div>

            <div className="relative pt-24 sm:pt-28 pb-16 px-5 sm:px-6">
                <div className="w-full max-w-[400px] mx-auto">
                    <div className="text-center mb-8 sm:mb-10">
                        <Link to="/" className="inline-block mb-6 transition-transform hover:scale-[1.03]" data-testid="register-brand-logo">
                            <PawLinkLogo variant="stacked" size={64} />
                        </Link>
                        <h1 className="font-display text-[32px] sm:text-[38px] leading-[1.1] font-medium tracking-tight text-paw-ink dark:text-white">
                            {t("auth.create")}
                        </h1>
                        <p className="mt-2.5 text-[15px] text-paw-ink2 dark:text-paw-silver">
                            {t("auth.create_sub")}
                        </p>
                    </div>

                    <div className="card-soft rounded-3xl p-6 sm:p-8 space-y-5 shadow-hover" data-testid="register-form-wrap">
                        <GoogleButton label={t("auth.google_signup")} testId="register-google-btn" />

                        <div className="flex items-center gap-4">
                            <div className="flex-1 h-px bg-paw-line dark:bg-white/10" />
                            <span className="text-[11px] uppercase tracking-[0.14em] text-paw-ink2/70 dark:text-paw-bone/40 font-semibold">
                                {t("auth.or_continue")}
                            </span>
                            <div className="flex-1 h-px bg-paw-line dark:bg-white/10" />
                        </div>

                        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
                            <div>
                                <label className="label-soft flex items-center gap-1.5 text-xs uppercase tracking-wider">
                                    <User size={12} weight="duotone" /> {t("auth.name")}
                                </label>
                                <input
                                    data-testid="register-name"
                                    type="text"
                                    className="input-soft mt-2 rounded-2xl h-12 text-[15px]"
                                    value={name} onChange={(e) => setName(e.target.value)}
                                    placeholder="Jane"
                                />
                            </div>
                            <div>
                                <label className="label-soft flex items-center gap-1.5 text-xs uppercase tracking-wider">
                                    <EnvelopeSimple size={12} weight="duotone" /> {t("auth.email")}
                                </label>
                                <input
                                    data-testid="register-email"
                                    type="email" required autoComplete="email"
                                    className="input-soft mt-2 rounded-2xl h-12 text-[15px]"
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                />
                            </div>
                            <div>
                                <label className="label-soft flex items-center gap-1.5 text-xs uppercase tracking-wider">
                                    <Lock size={12} weight="duotone" /> {t("auth.password")}
                                </label>
                                <input
                                    data-testid="register-password"
                                    type="password" required minLength={6} autoComplete="new-password"
                                    className="input-soft mt-2 rounded-2xl h-12 text-[15px]"
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    placeholder={t("auth.password_hint")}
                                />
                            </div>

                            {error && (
                                <div data-testid="register-error"
                                    className="rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 px-4 py-3 text-sm">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit" disabled={loading}
                                data-testid="register-submit"
                                className="btn-primary w-full h-12 rounded-2xl text-[15px] tracking-tight disabled:opacity-60"
                            >
                                {loading ? t("auth.creating") : t("auth.create_btn")}
                                <ArrowRight size={16} className="ms-1.5" />
                            </button>
                        </form>
                    </div>

                    <p className="text-center text-sm text-paw-ink2 dark:text-paw-bone/60 mt-6">
                        {t("auth.have_account")}{" "}
                        <Link to="/login" data-testid="register-goto-login"
                            className="text-paw-forest dark:text-paw-terra font-semibold hover:underline underline-offset-4">
                            {t("auth.sign_in")}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
